import { useTranslation } from "react-i18next";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Loader2, Download, MailCheck, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";

const TICK = { fontSize: 10 };
const TOOLTIP = { backgroundColor: "#243044", border: "1px solid #2d3f5a", borderRadius: 8 };
const LABEL = { color: "#f0e6d3" };
const BAR_RADIUS = [4, 4, 0, 0];
const shortDate = (value) => value.slice(5);

const Metric = ({ label, value, accent, testId }) => <div className="bg-card border border-border rounded-xl p-4" data-testid={testId}>
  <p className="text-xs text-muted-foreground">{label}</p>
  <p className={`text-2xl font-bold mt-1 ${accent || "text-foreground"}`}>{value}</p>
</div>;

export const AdminMetrics = ({ totals }) => {
  const { t } = useTranslation();
  return <div className="grid grid-cols-2 gap-3" data-testid="admin-metrics">
    <Metric label={t("admin.accounts")} value={totals.users} testId="admin-total-users" />
    <Metric label={t("admin.activated")} value={`${totals.activated} (${totals.activation_rate}%)`} accent="text-green-400" testId="admin-activated" />
    <Metric label={t("admin.pending")} value={totals.pending} accent="text-yellow-400" testId="admin-pending" />
    <Metric label={t("admin.analyses")} value={totals.analyses} accent="text-primary" testId="admin-analyses" />
    <Metric label={t("admin.facial")} value={totals.analyses_face} testId="admin-analyses-face" />
    <Metric label={t("admin.body")} value={totals.analyses_body} testId="admin-analyses-body" />
    <Metric label={t("admin.consented")} value={totals.consented} testId="admin-consented" />
    <Metric label={t("admin.records")} value={totals.weight_records + totals.imc_records} testId="admin-records" />
  </div>;
};

export const AdminChart = ({ title, data, icon: Icon, testId }) => <div className="bg-card border border-border rounded-xl p-5 space-y-3" data-testid={testId}>
  <div className="flex items-center gap-2"><Icon className="w-4 h-4 text-primary" /><h2 className="text-base md:text-lg font-semibold">{title}</h2></div>
  <div className="w-full min-w-0">
    <ResponsiveContainer width="100%" height={200}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#2d3f5a" />
        <XAxis dataKey="date" stroke="#8a9bb5" tick={TICK} tickFormatter={shortDate} />
        <YAxis stroke="#8a9bb5" tick={TICK} width={30} allowDecimals={false} />
        <Tooltip contentStyle={TOOLTIP} labelStyle={LABEL} />
        <Bar dataKey="count" fill="#c9a84c" radius={BAR_RADIUS} isAnimationActive={false} />
      </BarChart>
    </ResponsiveContainer>
  </div>
</div>;

export const AdminPlatforms = ({ platforms }) => {
  const { t } = useTranslation();
  return <div className="bg-card border border-border rounded-xl p-5 space-y-3" data-testid="admin-platforms">
    <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-primary" /><h2 className="text-base md:text-lg font-semibold">{t("admin.byPlatform")}</h2></div>
    <div className="space-y-2">
      {platforms.map((row) => <div key={row.platform} className="flex items-center justify-between gap-3 border-b border-border/40 pb-2 last:border-0" data-testid={`admin-platform-${row.platform}`}>
        <span className="font-medium capitalize">{row.platform}</span>
        <span className="text-sm text-muted-foreground text-right">
          {t("admin.platformRow", { total: row.total, activated: row.activated, pending: row.pending })}
        </span>
      </div>)}
    </div>
  </div>;
};

export const AdminActions = ({ busy, onExport, onCheckReminders, reminders }) => {
  const { t } = useTranslation();
  return <>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="admin-actions">
      <Button onClick={onExport} disabled={busy === "csv"} variant="outline" className="h-12 text-primary" data-testid="admin-export-csv">
        {busy === "csv" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}{t("admin.exportCsv")}
      </Button>
      <Button onClick={onCheckReminders} disabled={busy === "reminders"} variant="outline" className="h-12" data-testid="admin-check-reminders">
        {busy === "reminders" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MailCheck className="w-4 h-4 mr-2" />}{t("admin.checkReminders")}
      </Button>
    </div>
    {reminders && <p className="text-sm text-muted-foreground bg-card border border-border rounded-xl p-4" data-testid="admin-reminders-result">
      {t("admin.remindersDue", { count: reminders.due })}{" "}
      {reminders.enabled ? t("admin.remindersOn") : t("admin.remindersOff")}
    </p>}
  </>;
};

export const AdminLatest = ({ accounts, locale }) => {
  const { t } = useTranslation();
  return <div className="bg-card border border-border rounded-xl p-5 space-y-3" data-testid="admin-latest">
    <h2 className="text-base md:text-lg font-semibold">{t("admin.latestAccounts")}</h2>
    <div className="space-y-3">
      {accounts.map((account) => <div key={account.email} className="border-b border-border/40 pb-2 last:border-0">
        <p className="text-sm font-medium break-all">{account.email}</p>
        <p className="text-xs text-muted-foreground">
          {account.platform} · {account.activated ? t("admin.activeLabel") : t("admin.pendingLabel")}
          {account.created_at ? ` · ${new Date(account.created_at).toLocaleDateString(locale)}` : ""}
        </p>
      </div>)}
    </div>
  </div>;
};
